import { TRADE_KIND_COLORS } from '@/components/TradeHistoryTable/tradeKindColors';

import {
  findTradeMarkerGroupAtX,
  TRADE_MARKER_MERGE_DISTANCE,
  TRADE_MARKER_SNAP_THRESHOLD,
} from '@/ui/Charts/TimeSeriesChart/utils/geometry';

import {
  getTradeIdentity,
  getTradeType,
  isSuccessfulHistoryEntry,
  TRADE_TYPE,
} from '@/utils/0x/history/status';
import type { MatchaTradeHistoryEntry } from '@/utils/0x/history/types';
import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';

/** One execution on the portfolio value chart. Direction is not meaningful here. */
export type ActivityMarker = {
  key: string;
  timestamp: number;
  /** "1,000 USDC" and "0.3 ETH", built from the amounts that actually settled. */
  sold: string;
  bought: string;
  volumeUsd?: number;
  /** Set when the marker is one fill of an advanced order. */
  limitFill?: { fillIndex: number; fillCount: number };
  /** Advanced order type; undefined means a market swap. */
  orderType?: MatchaTradeHistoryEntry['advancedOrderType'];
  tradeType: TRADE_TYPE;
};

export type ActivityMarkerKind = 'swap' | 'bridge' | 'advanced';

/** Marker colors per kind, matching the trade type dots in the history table. */
export const ACTIVITY_MARKER_KINDS: Record<ActivityMarkerKind, { label: string; color: string }> = {
  swap: { label: 'Swap', color: TRADE_KIND_COLORS[TRADE_TYPE.MARKET].cssVar },
  bridge: { label: 'Bridge', color: TRADE_KIND_COLORS[TRADE_TYPE.CROSS_CHAIN].cssVar },
  advanced: { label: 'Advanced', color: TRADE_KIND_COLORS[TRADE_TYPE.LIMIT].cssVar },
};

const MARKER_KIND_BY_TRADE_TYPE: Record<TRADE_TYPE, ActivityMarkerKind> = {
  [TRADE_TYPE.MARKET]: 'swap',
  [TRADE_TYPE.MARKET_TRANSFER]: 'swap',
  [TRADE_TYPE.CROSS_CHAIN]: 'bridge',
  [TRADE_TYPE.LIMIT]: 'advanced',
};

export const getActivityMarkerKind = ({ tradeType }: ActivityMarker): ActivityMarkerKind =>
  MARKER_KIND_BY_TRADE_TYPE[tradeType];

/** A run of markers close enough on the x axis to share one glyph and tooltip. */
export type ActivityMarkerGroup = {
  key: string;
  markers: ActivityMarker[];
  x: number;
  y: number;
};

const amountFormatter = new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 });

/**
 * Maps one history row to a chart marker. Advanced orders report the whole
 * order in maker/takerAmount and what settled in the filled pair, so the
 * amounts use the filled side to match `volumeUSD`.
 */
export const tradeEntryToActivityMarker = (
  entry: MatchaTradeHistoryEntry,
): ActivityMarker | undefined => {
  if (!isSuccessfulHistoryEntry(entry)) return undefined;

  const timestamp = Date.parse(entry.timestamp);
  if (!Number.isFinite(timestamp)) return undefined;

  const isLimit = entry.liquiditySource === MATCHA_LIMIT_ORDER;
  const soldAmount = isLimit ? (entry.filledMakerAmount ?? entry.makerAmount) : entry.takerAmount;
  const boughtAmount = isLimit ? (entry.filledTakerAmount ?? entry.takerAmount) : entry.makerAmount;
  const soldSymbol = isLimit ? entry.makerTokenSymbol : entry.takerTokenSymbol;
  const boughtSymbol = isLimit ? entry.takerTokenSymbol : entry.makerTokenSymbol;

  return {
    key: getTradeIdentity(entry),
    timestamp,
    sold: `${amountFormatter.format(soldAmount)} ${soldSymbol}`,
    bought: `${amountFormatter.format(boughtAmount)} ${boughtSymbol}`,
    ...(entry.volumeUSD != null ? { volumeUsd: entry.volumeUSD } : undefined),
    ...(entry.limitFill ? { limitFill: entry.limitFill } : undefined),
    ...(entry.advancedOrderType ? { orderType: entry.advancedOrderType } : undefined),
    tradeType: getTradeType({
      isCrossChainTx: entry.destinationChainId != null,
      liquiditySource: entry.liquiditySource,
    }),
  };
};

/**
 * Places markers on the value line and merges pixel-adjacent ones, mirroring
 * how the token price chart groups its trade markers.
 */
export const buildActivityMarkerGroups = ({
  markers,
  xDomain,
  xScale,
  valueAt,
  yScale,
}: {
  markers: ActivityMarker[];
  /** Inclusive [min, max] timestamps covered by the value line. */
  xDomain: [number, number];
  xScale: (timestamp: number) => number;
  /** Portfolio value on the line at a timestamp. */
  valueAt: (timestamp: number) => number;
  yScale: (value: number) => number;
}): ActivityMarkerGroup[] => {
  const placed = markers
    .filter(({ timestamp }) => timestamp >= xDomain[0] && timestamp <= xDomain[1])
    .map((marker) => ({ marker, x: xScale(marker.timestamp) }))
    .toSorted((a, b) => a.x - b.x);

  const groups: ActivityMarkerGroup[] = [];
  for (const { marker, x } of placed) {
    const current = groups[groups.length - 1];
    if (current && x - current.x < TRADE_MARKER_MERGE_DISTANCE) {
      current.markers.push(marker);
      continue;
    }
    groups.push({ key: marker.key, markers: [marker], x, y: yScale(valueAt(marker.timestamp)) });
  }

  return groups.map((group) => ({
    ...group,
    key: group.markers.map(({ key }) => key).join('|'),
  }));
};

/** The group under the pointer, within the snap threshold of a marker on both axes. */
export const findActivityMarkerGroupAt = (
  groups: ActivityMarkerGroup[],
  x: number,
  y: number,
): ActivityMarkerGroup | undefined => {
  const group = findTradeMarkerGroupAtX(groups, x);
  return group && Math.abs(group.y - y) <= TRADE_MARKER_SNAP_THRESHOLD ? group : undefined;
};
